// Utility functions for distance-based operations
import { apiRequest } from '../api';
import appConfig from '../config/appConfig';

const buildRouteUrl = (profile, coordinates, options = 'overview=false') =>
    `${appConfig.api.routingBaseURL}/${profile}/${coordinates}?${options}`;

/**
 * Create a circular polygon around a center point with given radius
 * @param {Object} center - {lng, lat} coordinates
 * @param {number} radiusKm - Radius in kilometers
 * @param {number} points - Number of points to create the circle (default: 64)
 * @returns {Array} Array of [lng, lat] coordinates forming a circle
 */
export const createCirclePolygon = (center, radiusKm, points = 64) => {
    const coords = [];
    const earthRadius = 6371; // Earth's radius in km

    for (let i = 0; i <= points; i++) {
        const angle = (i * 360) / points;
        const angleRad = (angle * Math.PI) / 180;

        const lat = center.lat + (radiusKm / earthRadius) * (180 / Math.PI) * Math.cos(angleRad);
        const lng = center.lng + (radiusKm / earthRadius) * (180 / Math.PI) * Math.sin(angleRad) / Math.cos(center.lat * Math.PI / 180);

        coords.push([lng, lat]);
    }

    return coords;
};

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param {Array} point - [lng, lat] coordinates
 * @param {Array} polygon - Array of [lng, lat] coordinates forming the polygon
 * @returns {boolean} True if point is inside polygon
 */
export const isPointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];

        if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }

    return inside;
};

/**
 * Calculate distance between two points using Haversine formula
 * @param {Object} point1 - {lng, lat} coordinates
 * @param {Object} point2 - {lng, lat} coordinates
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (point1, point2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) * 
        Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
};

/**
 * Get actual route distance between two points using OSRM
 * @param {Object} origin - {lng, lat} coordinates
 * @param {Object} destination - {lng, lat} coordinates
 * @param {string} profile - OSRM profile (default: 'driving')
 * @returns {Promise<number>} Route distance in kilometers
 */
export const getRouteDistance = async (origin, destination, profile = 'driving') => {
    try {
        const response = await apiRequest(
            buildRouteUrl(profile, `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`)
        );
        const data = await response.json();

        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
            return data.routes[0].distance / 1000; // Convert to kilometers
        } else {
            throw new Error(data.message || 'No route found');
        }
    } catch (error) {

        // Fallback to straight-line distance
        return calculateDistance(origin, destination);
    }
};

/**
 * Validate service area by checking actual route distances to boundary points
 * @param {Object} center - Center point {lng, lat}
 * @param {Array} boundaryPoints - Array of boundary points [[lng, lat], ...]
 * @param {number} targetDistanceKm - Target distance in kilometers
 * @param {string} profile - OSRM profile
 * @returns {Promise<Object>} Validation results
 */
export const validateServiceArea = async (center, boundaryPoints, targetDistanceKm, profile = 'driving') => {

    const results = {
        validPoints: 0,
        invalidPoints: 0,
        averageDistance: 0,
        minDistance: Infinity,
        maxDistance: 0,
        details: []
    };

    let totalDistance = 0;
    const sampleSize = Math.min(boundaryPoints.length, 12); // Sample 12 points for validation
    const step = Math.floor(boundaryPoints.length / sampleSize);

    for (let i = 0; i < sampleSize; i++) {
        const pointIndex = i * step;
        const point = boundaryPoints[pointIndex];
        if (!point) continue;

        try {
            const routeDistance = await getRouteDistance(center, { lng: point[0], lat: point[1] }, profile);
            const isValid = Math.abs(routeDistance - targetDistanceKm) <= 0.2; // 200m tolerance

            if (isValid) {
                results.validPoints++;
            } else {
                results.invalidPoints++;
            }

            totalDistance += routeDistance;
            results.minDistance = Math.min(results.minDistance, routeDistance);
            results.maxDistance = Math.max(results.maxDistance, routeDistance);

            results.details.push({
                point: point,
                routeDistance: routeDistance,
                targetDistance: targetDistanceKm,
                difference: routeDistance - targetDistanceKm,
                isValid: isValid
            });

        } catch (error) {

            results.invalidPoints++;
        }
    }

    results.averageDistance = totalDistance / sampleSize;

    return results;
};

/**
 * Create a network-based service area using OSRM routing
 * This creates an actual service area based on road network distances/times
 * @param {Object} center - {lng, lat} coordinates
 * @param {number} distanceKm - Maximum travel distance in kilometers
 * @param {string} profile - OSRM profile (default: 'driving')
 * @param {number} directions - Number of directions to sample (default: 72)
 * @returns {Promise<Array>} Promise resolving to polygon coordinates
 */
export const createNetworkServiceArea = async (center, distanceKm, profile = 'driving', directions = 72) => {
    try {

        const serviceAreaPoints = [];
        const maxDistanceMeters = distanceKm * 1000; // Convert to meters
        const tolerance = 50; // 50 meters tolerance

        // Calculate points in multiple directions
        for (let i = 0; i < directions; i++) {
            const angle = (i * 360) / directions;
            const angleRad = (angle * Math.PI) / 180;

            // Binary search parameters
            let minDistance = 0.1; // Start from 100m
            let maxDistance = distanceKm * 2; // Search up to 2x the target distance
            let bestPoint = null;
            let iterations = 0;
            const maxIterations = 8; // More iterations for better precision

            // Binary search to find the furthest reachable point in this direction
            while (iterations < maxIterations && (maxDistance - minDistance) > 0.05) { // 50m precision
                const searchDistance = (minDistance + maxDistance) / 2;

                // Calculate target point at current search distance
                const targetLat = center.lat + (searchDistance / 111) * Math.cos(angleRad);
                const targetLng = center.lng + (searchDistance / (111 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angleRad);

                try {
                    const response = await apiRequest(
                        buildRouteUrl(profile, `${center.lng},${center.lat};${targetLng},${targetLat}`)
                    );
                    const data = await response.json();

                    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
                        const route = data.routes[0];
                        const routeDistance = route.distance; // Distance in meters

                        if (Math.abs(routeDistance - maxDistanceMeters) <= tolerance) {
                            // Very close to target distance - use this point
                            bestPoint = [targetLng, targetLat];
                            break;
                        } else if (routeDistance < maxDistanceMeters) {
                            // This point is reachable and within limit, try going further
                            bestPoint = [targetLng, targetLat];
                            minDistance = searchDistance;
                        } else {
                            // This point is too far, reduce search distance
                            maxDistance = searchDistance;
                        }
                    } else {
                        // No route found, reduce search distance
                        maxDistance = searchDistance;
                    }
                } catch (error) {

                    maxDistance = searchDistance;
                }

                iterations++;
            }

            // If we found a reachable point, use it; otherwise, use a fallback
            if (bestPoint) {
                serviceAreaPoints.push(bestPoint);

            } else {
                // Fallback: use a point at 60% of the target distance
                const fallbackDistance = distanceKm * 0.6;
                const fallbackLat = center.lat + (fallbackDistance / 111) * Math.cos(angleRad);
                const fallbackLng = center.lng + (fallbackDistance / (111 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angleRad);
                serviceAreaPoints.push([fallbackLng, fallbackLat]);

            }
        }

        // Smooth the polygon by adding intermediate points where there are large gaps
        const smoothedPoints = smoothServiceAreaPolygon(serviceAreaPoints);

        // Close the polygon
        if (smoothedPoints.length > 0) {
            smoothedPoints.push(smoothedPoints[0]);
        }

        return smoothedPoints;

    } catch (error) {

        // Fallback to simple circle if network calculation fails

        return createCirclePolygon(center, distanceKm);
    }
};

/**
 * Smooth a service area polygon by adding intermediate points where needed
 * @param {Array} points - Array of [lng, lat] coordinates
 * @returns {Array} Smoothed array of coordinates
 */
const smoothServiceAreaPolygon = (points) => {
    if (points.length < 3) return points;

    const smoothedPoints = [];
    const maxGapDistance = 0.01; // Maximum gap in degrees (roughly 1km)

    for (let i = 0; i < points.length; i++) {
        const currentPoint = points[i];
        const nextPoint = points[(i + 1) % points.length];

        smoothedPoints.push(currentPoint);

        // Calculate distance between current and next point
        const distance = Math.sqrt(
            Math.pow(nextPoint[0] - currentPoint[0], 2) + 
            Math.pow(nextPoint[1] - currentPoint[1], 2)
        );

        // If gap is too large, add intermediate points
        if (distance > maxGapDistance) {
            const numIntermediatePoints = Math.ceil(distance / maxGapDistance) - 1;
            for (let j = 1; j <= numIntermediatePoints; j++) {
                const ratio = j / (numIntermediatePoints + 1);
                const intermediateLng = currentPoint[0] + (nextPoint[0] - currentPoint[0]) * ratio;
                const intermediateLat = currentPoint[1] + (nextPoint[1] - currentPoint[1]) * ratio;
                smoothedPoints.push([intermediateLng, intermediateLat]);
            }
        }
    }

    return smoothedPoints;
};

/**
 * Create an isochrone (travel time polygon) using OSRM routing
 * @param {Object} center - {lng, lat} coordinates
 * @param {number} timeMinutes - Travel time in minutes
 * @param {string} profile - OSRM profile (default: 'driving')
 * @param {number} directions - Number of directions to sample (default: 24)
 * @returns {Promise<Array>} Promise resolving to polygon coordinates
 */
export const createIsochrone = async (center, timeMinutes, profile = 'driving', directions = 24) => {
    try {

        const isochronePoints = [];
        const maxTimeSeconds = timeMinutes * 60; // Convert to seconds

        // Calculate points in multiple directions
        for (let i = 0; i < directions; i++) {
            const angle = (i * 360) / directions;
            const angleRad = (angle * Math.PI) / 180;

            // Start with a reasonable initial distance estimate (assume 50 km/h average)
            let searchDistance = (timeMinutes / 60) * 50; // km
            let bestPoint = null;
            let iterations = 0;
            const maxIterations = 4;

            // Binary search to find the furthest reachable point in this direction within time limit
            while (iterations < maxIterations) {
                // Calculate target point at current search distance
                const targetLat = center.lat + (searchDistance / 111) * Math.cos(angleRad);
                const targetLng = center.lng + (searchDistance / (111 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angleRad);

                try {
                    const response = await apiRequest(
                        buildRouteUrl(profile, `${center.lng},${center.lat};${targetLng},${targetLat}`)
                    );
                    const data = await response.json();

                    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
                        const route = data.routes[0];
                        const routeTime = route.duration; // Time in seconds

                        if (routeTime <= maxTimeSeconds) {
                            // This point is reachable within time, try going further
                            bestPoint = [targetLng, targetLat];
                            searchDistance *= 1.3; // Increase search distance
                        } else {
                            // This point takes too long, reduce search distance
                            searchDistance *= 0.7;
                        }
                    } else {
                        // No route found, reduce search distance
                        searchDistance *= 0.6;
                    }
                } catch (error) {

                    searchDistance *= 0.6;
                }

                iterations++;
            }

            // If we found a reachable point, use it; otherwise, use a fallback
            if (bestPoint) {
                isochronePoints.push(bestPoint);
            } else {
                // Fallback: use a point at 60% of estimated distance
                const fallbackDistance = (timeMinutes / 60) * 30; // Assume 30 km/h for fallback
                const fallbackLat = center.lat + (fallbackDistance / 111) * Math.cos(angleRad);
                const fallbackLng = center.lng + (fallbackDistance / (111 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angleRad);
                isochronePoints.push([fallbackLng, fallbackLat]);

            }
        }

        // Close the polygon
        if (isochronePoints.length > 0) {
            isochronePoints.push(isochronePoints[0]);
        }

        return isochronePoints;

    } catch (error) {

        // Fallback to simple circle

        return createCirclePolygon(center, (timeMinutes / 60) * 40); // Assume 40 km/h average
    }
};
